import { CommonModule } from '@angular/common';
import { Component, inject, input, Input, ChangeDetectionStrategy } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { IScheduleUser, Site } from 'src/app/models/site.model';
import { TimesheetsService } from 'src/app/services/timesheets.service';
import { UserSessionService } from 'src/app/services/user-session.service';
import { Roles } from 'src/app/utility/role';

@Component({
  selector: 'app-extra-persons',
  templateUrl: './extra-person.component.html',
  styleUrls: ['./extra-person.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    AppCommonModule,
  ],
})
export class ExtraPersonsComponent {
  private timesheetsService = inject(TimesheetsService);
  private usersService = inject(UserSessionService);

  private date$: ReplaySubject<Date> = new ReplaySubject(1);
  private workers$: BehaviorSubject<IScheduleUser[]> = new BehaviorSubject([]);

  site = input<Site>();

  @Input()
  set date(value: Date) {
    this.date$.next(value);
  }

  @Input()
  set workers(value: IScheduleUser[]) {
    this.workers$.next(value);
  }

  persons$: Observable<string[]> = combineLatest([this.date$.asObservable(), this.usersService.getCurrentUser()]).pipe(
    filter(([_, user]) => user != null),
    switchMap(([date, user]) => this.timesheetsService.getList({
      site: this.site().guid, startDate: date, endDate: date, user: [Roles.worker].includes(user.role) ? user.guid : null,
    })),
    switchMap((timesheets) => combineLatest([of(timesheets), this.workers$.asObservable()])),
    map(([timesheets, workers]) => {
      const workerGuids = workers.map((w) => w.guid);
      const extraTs = timesheets.filter((ts) => !workerGuids.includes(ts.user));
      return extraTs.map((ts) => ts.username ?? ts.user);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
}
